import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Button,
    Form,
    Image,
    Modal,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../../styles/Employees/employeeLasttracked.scss";

import { Employee } from "./utils";
import FilterComponent from "../FilterComponent";

import {
    commonGetService,
    commonGetBlobService,
} from "../../../utils/properties";

import {
    useAppDispatch,
    useAppSelector,
} from "../../../app/hooks";

import {
    setDepartmentLists,
    setDesignationLists,
    setEmployeeSearch,
    setEmployeeStatusLists,
} from "../../../features/employee/employeeListSlice";

import {
    Pagination,
    PaginationProps,
} from "antd";

import Sorter from "../EmployeePerformance/Sorter";

type SortOrder = "asc" | "desc" | null;
type OptionKey = "value" | "filterValue";

export interface FilterOption {
    filterValue: string;
    isSelect: boolean;
    value?: string;
    title?: string;
}
const parseFilterDate = (
    value?: string
): Date | null => {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? null
        : date;
};

const formatDateTimeForApi = (
    date: Date | null
): string => {
    if (!date) {
        return "";
    }

    const pad = (value: number) =>
        String(value).padStart(2, "0");

    return `${date.getFullYear()}-${pad(
        date.getMonth() + 1
    )}-${pad(date.getDate())}T${pad(
        date.getHours()
    )}:${pad(date.getMinutes())}`;
};

const formatDateTimeLabel = (value: string) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const isSameDay = (firstDate: Date, secondDate: Date) => {
    return (
        firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth() &&
        firstDate.getDate() === secondDate.getDate()
    );
};

const getStartOfDay = (date: Date) => {
    const start = new Date(date);

    start.setHours(0, 0, 0, 0);

    return start;
};

const getEndOfDay = (date: Date) => {
    const end = new Date(date);

    end.setHours(23, 59, 59, 999);

    return end;
};

const getOptionKeyValue = (
    option: FilterOption,
    key: OptionKey
): string | undefined =>
    key === "value" ? option.value : option.filterValue;

const extractSelectedValues = (
    options: FilterOption[],
    key: OptionKey
): string[] =>
    options
        .filter((option) => option.isSelect)
        .map((option) => getOptionKeyValue(option, key))
        .filter((value): value is string => value !== undefined);

const applySelectionFromSaved = (
    options: FilterOption[],
    key: OptionKey,
    savedValues: string[]
): FilterOption[] =>
    options.map((option) => ({
        ...option,
        isSelect: savedValues.includes(
            getOptionKeyValue(option, key) ?? ""
        ),
    }));

const cloneOptions = (options: FilterOption[]): FilterOption[] =>
    options.map((option) => ({ ...option }));

const clearOptions = (options: FilterOption[]): FilterOption[] =>
    options.map((option) => ({ ...option, isSelect: false }));

const parseCsvLine = (line: string): string[] => {
    const fields: string[] = [];
    let current = "";
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
            if (insideQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                insideQuotes = !insideQuotes;
            }
        } else if (char === "," && !insideQuotes) {
            fields.push(current);
            current = "";
        } else {
            current += char;
        }
    }

    fields.push(current);

    return fields;
};

const sanitizeExportCsv = (csvText: string): string => {
    const withoutBom = csvText.replace(/^\uFEFF/, "");
    const normalized = withoutBom.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

    return normalized
        .split("\n")
        .map((line) => {
            if (!line) {
                return line;
            }

            const fields = parseCsvLine(line);

            const cleanedFields = fields.map((field) =>
                field.replace(/,/g, " ").replace(/\s+/g, " ").trim()
            );

            return cleanedFields.join(",");
        })
        .join("\n");
};

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const renderDatePickerHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
}: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
}) => (
    <div className="employee-lasttracked-datepicker-header">
        <button
            type="button"
            className="datepicker-header-arrow button-left"
            onClick={(e) => {
                e.stopPropagation();
                decreaseMonth();
            }}
            disabled={prevMonthButtonDisabled}
            aria-label="Previous month"
        >
            <Image src="/images/dashboard/LeftArrow.svg" alt="Previous" />
        </button>

        <h6>
            {MONTH_NAMES[date.getMonth()]} {date.getFullYear()}
        </h6>

        <button
            type="button"
            className="datepicker-header-arrow button-right"
            onClick={(e) => {
                e.stopPropagation();
                increaseMonth();
            }}
            disabled={nextMonthButtonDisabled}
            aria-label="Next month"
        >
            <Image src="/images/dashboard/rightArrow.svg" alt="Next" />
        </button>
    </div>
);

const getMaxTime = (selectedDate: Date | null) => {
    const now = new Date();

    if (!selectedDate) {
        return getEndOfDay(now);
    }

    return isSameDay(selectedDate, now) ? now : getEndOfDay(selectedDate);
};

const isSelectableTime = (time: Date, selectedDate: Date | null) => {
    if (!selectedDate) {
        return true;
    }

    const now = new Date();

    if (!isSameDay(selectedDate, now)) {
        return true;
    }

    return time.getTime() <= now.getTime();
};

interface FilterDropdownProps {
    id: string;
    label: string;
    placeholder: string;
    options: FilterOption[];
    setter: React.Dispatch<React.SetStateAction<FilterOption[]>>;
    optionKey: OptionKey;
    isOpen: boolean;
    onToggleDropdown: (id: string) => void;
    onToggleOption: (
        setter: React.Dispatch<React.SetStateAction<FilterOption[]>>,
        key: OptionKey,
        keyVal: string
    ) => void;
}

const FilterDropdown = React.memo(function FilterDropdown({
    id,
    label,
    placeholder,
    options,
    setter,
    optionKey,
    isOpen,
    onToggleDropdown,
    onToggleOption,
}: FilterDropdownProps) {
    const selectedItems = useMemo(
        () => options.filter((option) => option.isSelect),
        [options]
    );

    const selectedCount = selectedItems.length;

    const removeChip = (event: React.MouseEvent, keyVal: string) => {
        event.stopPropagation();
        onToggleOption(setter, optionKey, keyVal);
    };

    return (
        <Form.Group className="mb-3 filter-dropdown-group">
            <Form.Label>{label}</Form.Label>

            <div className={`filter-dropdown${isOpen ? " open" : ""}`}>
                <button
                    type="button"
                    className={`filter-dropdown-trigger${selectedCount > 0 ? " has-chips" : ""
                        }`}
                    onClick={() => onToggleDropdown(id)}
                >
                    <span className="trigger-chips-area">
                        {selectedCount > 0 ? (
                            selectedItems.map((option) => {
                                const keyVal =
                                    getOptionKeyValue(option, optionKey) ?? "";

                                return (
                                    <span key={keyVal} className="filter-chip">
                                        <span className="chip-label">
                                            {option.filterValue}
                                        </span>

                                        <span
                                            className="chip-remove"
                                            onClick={(event) =>
                                                removeChip(event, keyVal)
                                            }
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M4 4L12 12M12 4L4 12"
                                                    stroke="#A0A4A8"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </span>
                                    </span>
                                );
                            })
                        ) : (
                            <span className="trigger-placeholder">
                                {placeholder}
                            </span>
                        )}
                    </span>

                    <span className="trigger-right">
                        <svg
                            className={`dropdown-chevron${isOpen ? " rotated" : ""
                                }`}
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                        >
                            <path
                                d="M4 6L8 10L12 6"
                                stroke="#6d6d6d"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                </button>

                {isOpen && (
                    <div className="filter-dropdown-panel">
                        {options.length === 0 ? (
                            <p className="no-options-text">
                                No options available
                            </p>
                        ) : (
                            options.map((option) => {
                                const keyVal =
                                    getOptionKeyValue(option, optionKey) ?? "";

                                return (
                                    <label
                                        key={keyVal}
                                        className={`dropdown-checkbox-row${option.isSelect ? " checked" : ""
                                            }`}
                                        onClick={() =>
                                            onToggleOption(
                                                setter,
                                                optionKey,
                                                keyVal
                                            )
                                        }
                                    >
                                        <span
                                            className={`custom-checkbox${option.isSelect
                                                ? " checked"
                                                : ""
                                                }`}
                                        >
                                            {option.isSelect && (
                                                <svg
                                                    width="10"
                                                    height="8"
                                                    viewBox="0 0 10 8"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M1 4L3.5 6.5L9 1"
                                                        stroke="#fff"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </span>

                                        <span className="dropdown-checkbox-label">
                                            {option.filterValue}
                                        </span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </Form.Group>
    );
});

const EmployeeLasttracked = () => {
    const dispatch = useAppDispatch();

    const employeeListData = useAppSelector(
        (state) => state.employeeList
    );

    const controllerRef = useRef<AbortController | null>(null);

    const tabletDatePickerRef = useRef<DatePicker | null>(null);

    const [departmentOptions, setDepartmentOptions] = useState<
        FilterOption[]
    >([]);

    const [designationOptions, setDesignationOptions] = useState<
        FilterOption[]
    >([]);

    const [employeeStatus, setEmployeeStatus] = useState<FilterOption[]>([
        { filterValue: "Active", isSelect: false, value: "Active" },
        { filterValue: "Exited", isSelect: false, value: "Exited" },
    ]);

    const [empStatusSelected, setEmpStatusSelected] = useState<string[]>(
        []
    );

    const [designationSelected, setDesignationSelected] = useState<
        string[]
    >([]);

    const [departmentSelected, setDepartmentSelected] = useState<
        string[]
    >([]);

    const [lastTrackedLessThan, setLastTrackedLessThan] =
        useState<string>("");

    const [sortOrder, setSortOrder] = useState<SortOrder>(null);

    const [employees, setEmployees] = useState<Employee[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const [searchText, setSearchText] = useState<string>(
        employeeListData?.search || ""
    );

    const [debouncedSearch, setDebouncedSearch] = useState<string>(
        employeeListData?.search || ""
    );

    const [current, setCurrent] = useState<number>(1);

    const [totalEmployees, setTotalEmployees] = useState<number>(0);

    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const [tempDepartmentOptions, setTempDepartmentOptions] = useState<
        FilterOption[]
    >([]);

    const [tempDesignationOptions, setTempDesignationOptions] = useState<
        FilterOption[]
    >([]);

    const [tempEmployeeStatus, setTempEmployeeStatus] = useState<
        FilterOption[]
    >([]);

    const [tempLastTrackedLessThan, setTempLastTrackedLessThan] =
        useState<string>("");

    const handleDatePickerChange = useCallback(
        (
            date: Date | null,
            setter: React.Dispatch<React.SetStateAction<string>>,
            resetPage = false
        ) => {
            if (!date) {
                setter("");

                if (resetPage) {
                    setCurrent(1);
                }

                return;
            }

            const now = new Date();

            if (date.getTime() > now.getTime()) {
                return;
            }

            setter(formatDateTimeForApi(date));

            if (resetPage) {
                setCurrent(1);
            }
        },
        []
    );

    const handleLastTrackedFilterChange = useCallback(
        (date: Date | null) => {
            handleDatePickerChange(date, setLastTrackedLessThan, true);
        },
        [handleDatePickerChange]
    );

    const handleTempLastTrackedFilterChange = useCallback(
        (date: Date | null) => {
            handleDatePickerChange(date, setTempLastTrackedLessThan);
        },
        [handleDatePickerChange]
    );


    useEffect(() => {
        const loadFilters = async () => {
            try {
                const [departmentRes, designationRes] = await Promise.all([
                    commonGetService("/api/1.0/role/departments"),
                    commonGetService("/api/1.0/employee/designations"),
                ]);

                if (departmentRes.status === 200) {
                    const selectedDepartments =
                        employeeListData?.departmentLists || [];

                    const options: FilterOption[] = (
                        departmentRes.data || []
                    ).map((item: any) => ({
                        filterValue: item.label,
                        isSelect: selectedDepartments.includes(item.value),
                        value: item.value,
                    }));

                    setDepartmentOptions(options);
                    setDepartmentSelected(
                        extractSelectedValues(options, "value")
                    );
                } else {
                    setDepartmentOptions([]);
                }

                if (designationRes.status === 200) {
                    const selectedDesignations =
                        employeeListData?.designationLists || [];

                    const options: FilterOption[] = (
                        designationRes.data || []
                    ).map((item: any) => ({
                        filterValue: item.label,
                        isSelect: selectedDesignations.includes(item.value),
                        value: item.value,
                    }));

                    setDesignationOptions(options);
                    setDesignationSelected(
                        extractSelectedValues(options, "value")
                    );
                } else {
                    setDesignationOptions([]);
                }

                const savedEmployeeStatuses =
                    employeeListData?.employeeStatusLists || [];

                setEmployeeStatus((previous) => {
                    const updated = applySelectionFromSaved(
                        previous,
                        "filterValue",
                        savedEmployeeStatuses
                    );

                    setEmpStatusSelected(
                        extractSelectedValues(updated, "filterValue")
                    );

                    return updated;
                });
            } catch {
                setDepartmentOptions([]);
                setDesignationOptions([]);
            }
        };

        loadFilters();
    }, []);


    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchText);
            setCurrent(1);
        }, 400);

        return () => clearTimeout(handler);
    }, [searchText]);


    useEffect(() => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        const controller = new AbortController();
        controllerRef.current = controller;

        const page = Math.max(current - 1, 0);

        const params = new URLSearchParams();

        params.append("page", String(page));
        params.append("size", "10");

        if (empStatusSelected.length > 0) {
            params.append("emp_status", empStatusSelected.join(","));
        }

        if (designationSelected.length > 0) {
            params.append(
                "designation_ids",
                designationSelected.join(",")
            );
        }

        if (departmentSelected.length > 0) {
            params.append("department_ids", departmentSelected.join(","));
        }

        if (debouncedSearch.trim()) {
            params.append("search", debouncedSearch.trim());
        }

        if (lastTrackedLessThan) {
            params.append("last_tracked_before", lastTrackedLessThan);
        }

        if (sortOrder) {
            params.append("sort_direction", sortOrder.toUpperCase());
        }

        const endpoint = `/api/1.0/employee/last-track-activity?${params.toString()}`;

        commonGetService(endpoint, controller.signal)
            .then((res) => {
                if (controller.signal.aborted) {
                    return;
                }

                if (res.status === 200) {
                    const responseData = res.data;

                    const content = Array.isArray(responseData?.content)
                        ? responseData.content
                        : [];

                    setEmployees(content);
                    setTotalEmployees(
                        Number(responseData?.totalElements) || 0
                    );
                } else {
                    setEmployees([]);
                    setTotalEmployees(0);
                }

                setIsLoading(false);
            })
            .catch((error) => {
                if (
                    controller.signal.aborted ||
                    error?.name === "AbortError"
                ) {
                    return;
                }

                setEmployees([]);
                setTotalEmployees(0);
                setIsLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, [
        departmentSelected,
        designationSelected,
        empStatusSelected,
        current,
        debouncedSearch,
        lastTrackedLessThan,
        sortOrder,
    ]);

    const openFilterModal = useCallback(() => {
        setTempDepartmentOptions(cloneOptions(departmentOptions));
        setTempDesignationOptions(cloneOptions(designationOptions));
        setTempEmployeeStatus(cloneOptions(employeeStatus));
        setTempLastTrackedLessThan(lastTrackedLessThan);

        setOpenDropdown(null);
        setShowFilterModal(true);
    }, [
        departmentOptions,
        designationOptions,
        employeeStatus,
        lastTrackedLessThan,
    ]);

    const handleModalCancel = useCallback(() => {
        setShowFilterModal(false);
        setOpenDropdown(null);
    }, []);

    const handleModalApply = useCallback(() => {
        const selectedDepartments = extractSelectedValues(
            tempDepartmentOptions,
            "value"
        );

        const selectedDesignations = extractSelectedValues(
            tempDesignationOptions,
            "value"
        );

        const selectedEmployeeStatuses = extractSelectedValues(
            tempEmployeeStatus,
            "filterValue"
        );

        setDepartmentOptions(tempDepartmentOptions);
        setDesignationOptions(tempDesignationOptions);
        setEmployeeStatus(tempEmployeeStatus);

        setDepartmentSelected(selectedDepartments);
        setDesignationSelected(selectedDesignations);
        setEmpStatusSelected(selectedEmployeeStatuses);

        setLastTrackedLessThan(tempLastTrackedLessThan);

        dispatch(setDepartmentLists(selectedDepartments));
        dispatch(setDesignationLists(selectedDesignations));
        dispatch(setEmployeeStatusLists(selectedEmployeeStatuses));

        setCurrent(1);
        setShowFilterModal(false);
        setOpenDropdown(null);
    }, [
        tempDepartmentOptions,
        tempDesignationOptions,
        tempEmployeeStatus,
        tempLastTrackedLessThan,
        dispatch,
    ]);


    const handleClearAll = useCallback(() => {
        setDepartmentOptions((previous) => clearOptions(previous));
        setDesignationOptions((previous) => clearOptions(previous));
        setEmployeeStatus((previous) => clearOptions(previous));
        setDepartmentSelected([]);
        setDesignationSelected([]);
        setEmpStatusSelected([]);
        setLastTrackedLessThan("");
        setTempLastTrackedLessThan("");
        dispatch(setDepartmentLists([]));
        dispatch(setDesignationLists([]));
        dispatch(setEmployeeStatusLists([]));

        setCurrent(1);
    }, [dispatch]);


    const makeDesktopFilterHandler = useCallback(
        (
            setOptions: React.Dispatch<React.SetStateAction<FilterOption[]>>,
            setSelected: React.Dispatch<React.SetStateAction<string[]>>,
            dispatchActionCreator: (values: string[]) => any,
            key: OptionKey
        ) => (updatedOptions: FilterOption[]) => {
            const selected = extractSelectedValues(updatedOptions, key);

            setOptions([...updatedOptions]);
            setSelected(selected);
            dispatch(dispatchActionCreator(selected));
            setCurrent(1);
        },
        [dispatch]
    );

    const handleDepartmentApply = useMemo(
        () =>
            makeDesktopFilterHandler(
                setDepartmentOptions,
                setDepartmentSelected,
                setDepartmentLists,
                "value"
            ),
        [makeDesktopFilterHandler]
    );

    const handleDesignationApply = useMemo(
        () =>
            makeDesktopFilterHandler(
                setDesignationOptions,
                setDesignationSelected,
                setDesignationLists,
                "value"
            ),
        [makeDesktopFilterHandler]
    );

    const hasActiveFilters =
        departmentSelected.length > 0 ||
        designationSelected.length > 0 ||
        empStatusSelected.length > 0 ||
        Boolean(lastTrackedLessThan);
    const tableRows = employees;

    const handleExport = useCallback(async () => {
        try {
            setIsExporting(true);

            const params = new URLSearchParams();

            if (empStatusSelected.length > 0) {
                params.append("emp_status", empStatusSelected.join(","));
            }
            if (designationSelected.length > 0) {
                params.append("designation_ids", designationSelected.join(","));
            }
            if (departmentSelected.length > 0) {
                params.append("department_ids", departmentSelected.join(","));
            }
            if (debouncedSearch.trim()) {
                params.append("search", debouncedSearch.trim());
            }
            if (lastTrackedLessThan) {
                params.append("last_tracked_before", lastTrackedLessThan);
            }
            if (sortOrder) {
                params.append("sort_direction", sortOrder.toUpperCase());
            }

            const endpoint = `/api/1.0/employee/last-track-activity/export?${params.toString()}`;

            const todayStr = new Date().toISOString().slice(0, 10); 
            const defaultFilename = `employee_last_track_activity_${todayStr}.csv`;

            const { blob, filename } = await commonGetBlobService(endpoint, defaultFilename);

            const rawText = await blob.text();
            const sanitizedText = sanitizeExportCsv(rawText);

            const sanitizedBlob = new Blob(["\uFEFF" + sanitizedText], {
                type: "text/csv;charset=utf-8;",
            });

            const url = URL.createObjectURL(sanitizedBlob);
            const link = document.createElement("a");

            link.href = url;
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Employee last tracked export failed:", error);
        } finally {
            setIsExporting(false);
        }
    }, [
        empStatusSelected,
        designationSelected,
        departmentSelected,
        debouncedSearch,
        lastTrackedLessThan,
        sortOrder,
    ]);

    const toggleDropdown = useCallback((key: string) => {
        setOpenDropdown((previous) => (previous === key ? null : key));
    }, []);

    const handleSortLastTracked = useCallback((order: string) => {
        if (order !== "asc" && order !== "desc") {
            return;
        }

        setSortOrder(order as "asc" | "desc");
        setCurrent(1);
    }, []);

    const onPageChange = useCallback<
        NonNullable<PaginationProps["onChange"]>
    >((page) => {
        setCurrent(page);
    }, []);

    const toggleTempOption = useCallback(
        (
            setter: React.Dispatch<React.SetStateAction<FilterOption[]>>,
            key: OptionKey,
            keyVal: string
        ) => {
            setter((previous) =>
                previous.map((option) => {
                    const currentValue = getOptionKeyValue(option, key);

                    return currentValue === keyVal
                        ? { ...option, isSelect: !option.isSelect }
                        : option;
                })
            );
        },
        []
    );

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            setSearchText(value);
            dispatch(setEmployeeSearch(value));
        },
        [dispatch]
    );

    const lastTrackedFilterDate = useMemo(
        () => parseFilterDate(lastTrackedLessThan),
        [lastTrackedLessThan]
    );

    const tempLastTrackedFilterDate = useMemo(
        () => parseFilterDate(tempLastTrackedLessThan),
        [tempLastTrackedLessThan]
    );

    const [pendingLastTrackedDate, setPendingLastTrackedDate] =
        useState<Date | null>(null);

    const handleLastTrackedDateSelect = useCallback((date: Date | null) => {
        if (date) {
            const now = new Date();

            if (date.getTime() > now.getTime()) {
                return;
            }
        }

        setPendingLastTrackedDate(date);
    }, []);

    const openLastTrackedCalendar = useCallback(() => {
        setPendingLastTrackedDate(lastTrackedFilterDate);
    }, [lastTrackedFilterDate]);

    const commitLastTrackedFilter = useCallback(() => {
        handleLastTrackedFilterChange(pendingLastTrackedDate);
    }, [pendingLastTrackedDate, handleLastTrackedFilterChange]);


    return (
        <div className="emp-container">
            {isLoading ? (
                <div className="d-flex justify-content-center align-items-center h-100 w-100 crt-loader">
                    <img
                        src="/images/loader/content_loader.gif"
                        alt="Loading"
                    />
                </div>
            ) : (
                <div className="list-emp-contaniner d-flex flex-column">
                    {/* Header */}

                    <div className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-center">
                            <h3>Employee Last Tracked</h3>

                            <Button
                                className="export-btn-list-emp desktop-export-btn custom-export-btn"
                                onClick={handleExport}
                                disabled={isExporting || !tableRows.length}
                            >
                                <Image
                                    src="/images/employees/download_icon.svg"
                                    alt="Download"
                                />
                                Export
                            </Button>
                        </div>

                        <div className="search-and-actions">
                            <div className="search-bar">
                                <Form.Control
                                    type="text"
                                    placeholder="Search employees by name, ID, or department"
                                    value={searchText}
                                    name="employee-filter"
                                    onChange={handleSearchChange}
                                />

                                <Image
                                    src="/images/dashboard/grey_search.svg"
                                    alt="Search"
                                    className="position-absolute"
                                />
                            </div>

                            <div className="tablet-actions">
                                <Button
                                    className="tablet-filter-btn"
                                    onClick={openFilterModal}
                                >
                                    <Image
                                        src="/images/employees/filtericon.svg"
                                        alt="Filter"
                                    />

                                    Filter

                                    {hasActiveFilters && (
                                        <span className="filter-badge">
                                            {departmentSelected.length +
                                                designationSelected.length +
                                                empStatusSelected.length}
                                        </span>
                                    )}
                                </Button>

                                <Button
                                    className="export-btn-list-emp custom-export-btn"
                                    onClick={handleExport}
                                    disabled={isExporting || !tableRows.length}
                                >
                                    <Image
                                        src="/images/employees/download_icon.svg"
                                        alt="Download"
                                    />
                                    Export
                                </Button>
                            </div>
                        </div>

                        <div className="fltr-fld-cont desktop-filters">
                            <div className="fltr-fld">
                                <FilterComponent
                                    filterBy="Department"
                                    filterTitle="Select Department"
                                    filterOptions={departmentOptions}
                                    filterType="checkbox"
                                    onApply={handleDepartmentApply}
                                />

                                <FilterComponent
                                    filterBy="Designation"
                                    filterTitle="Select Designation"
                                    filterOptions={designationOptions}
                                    filterType="checkbox"
                                    onApply={handleDesignationApply}
                                />

                                <div className="last-tracked-filter">
                                    <div className="last-tracked-filter-control">
                                        <Image
                                            src="/images/dashboard/calendar_icon.svg"
                                            alt="Calendar"
                                            className="last-tracked-calendar-icon"
                                        />

                                        <DatePicker
                                            id="last-tracked-less-than"
                                            selected={
                                                pendingLastTrackedDate ?? lastTrackedFilterDate
                                            }
                                            onChange={handleLastTrackedDateSelect}
                                            onCalendarOpen={openLastTrackedCalendar}
                                            onCalendarClose={commitLastTrackedFilter}
                                            renderCustomHeader={renderDatePickerHeader}
                                            showTimeSelect
                                            timeIntervals={1}
                                            timeCaption="Time"
                                            dateFormat="dd-MM-yyyy hh:mm aa"
                                            placeholderText="dd-MM-yyyy hh:mm AM/PM"
                                            maxDate={new Date()}
                                            minTime={getStartOfDay(
                                                (pendingLastTrackedDate ?? lastTrackedFilterDate) ||
                                                new Date()
                                            )}
                                            maxTime={getMaxTime(
                                                pendingLastTrackedDate ?? lastTrackedFilterDate
                                            )}
                                            filterTime={(time) =>
                                                isSelectableTime(
                                                    time,
                                                    pendingLastTrackedDate ?? lastTrackedFilterDate
                                                )
                                            }
                                            popperPlacement="bottom-start"
                                            popperClassName="employee-lasttracked-popper"
                                            calendarClassName="employee-lasttracked-calendar"
                                            wrapperClassName="employee-lasttracked-datepicker-wrapper"
                                            autoComplete="off"
                                        />

                                        {lastTrackedLessThan && (
                                            <button
                                                type="button"
                                                className="last-tracked-clear"
                                                onClick={() => {
                                                    setPendingLastTrackedDate(null);
                                                    handleLastTrackedFilterChange(null);
                                                }}
                                                aria-label="Clear last tracked filter"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {hasActiveFilters && (
                                <div
                                    className="clr-txt"
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="emp-list w-100 common-scroll mx-auto styled-scroll-hor">
                        <table className="w-100">
                            <thead>
                                <tr>
                                    <th>Emp ID</th>
                                    <th>Employee Name</th>
                                    <th>Department</th>
                                    <th>Designation</th>

                                    <th>
                                        <div className="last-tracked-sort-header">
                                            <span>Last Tracked Time</span>

                                            <Sorter
                                                onClick={
                                                    handleSortLastTracked
                                                }
                                            />
                                        </div>
                                    </th>

                                    <th>Last Screenshot At</th>
                                </tr>
                            </thead>

                            <tbody>
                                {tableRows.length > 0 ? (
                                    tableRows.map((item) => (
                                        <tr key={item.employee_id}>
                                            <td>{item.employee_id}</td>

                                            <td>
                                                <div className="d-flex">
                                                    {item.employee_image ? (
                                                        <div className="image-wrapper">
                                                            <Image
                                                                src={
                                                                    item.employee_image
                                                                }
                                                                alt={
                                                                    item.employee_name
                                                                }
                                                                className="emp-img"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="pro-pic fill-color-avatar">
                                                            <span>
                                                                {item.employee_name
                                                                    ?.charAt(0)
                                                                    .toUpperCase()}
                                                            </span>
                                                        </div>
                                                    )}

                                                    <span>
                                                        <p
                                                            className="emp-name"
                                                            title={
                                                                item.employee_name
                                                            }
                                                        >
                                                            {
                                                                item.employee_name
                                                            }
                                                        </p>

                                                        <p
                                                            title={
                                                                item.employee_mail
                                                            }
                                                        >
                                                            {
                                                                item.employee_mail
                                                            }
                                                        </p>
                                                    </span>
                                                </div>
                                            </td>

                                            <td title={item.department}>
                                                {item.department}
                                            </td>

                                            <td title={item.designation}>
                                                {item.designation}
                                            </td>

                                            <td className="last-tracked-time">
                                                {item.last_track_data ? (
                                                    <>
                                                        <p className="tracked-date">
                                                            {
                                                                item.last_track_data
                                                            }
                                                        </p>

                                                        <p className="tracked-ago">
                                                            {
                                                                item.last_track_data_ago
                                                            }
                                                        </p>
                                                    </>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td className="last-tracked-time last-screenshot-time">
                                                {item.last_screenshot_capture ? (
                                                    <>
                                                        <p className="tracked-date">{item.last_screenshot_capture}</p>
                                                        <p className="tracked-ago">{item.last_screenshot_capture_ago}</p>
                                                    </>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr className="no-data-row">
                                        <td className="no-data" colSpan={6}>
                                            <div className="d-flex flex-column justify-content-center align-items-center">
                                                <Image
                                                    src="/images/dashboard/user_icon.png"
                                                    alt="No Data Found"
                                                />

                                                <p
                                                    className="m-0"
                                                    style={{
                                                        paddingLeft: "3rem",
                                                    }}
                                                >
                                                    No users to show
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalEmployees > 0 && (
                        <div className="d-flex justify-content-end mt-3">
                            <Pagination
                                className="employee-pagination"
                                current={current}
                                onChange={onPageChange}
                                total={totalEmployees}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </div>
            )}

            <Modal
                show={showFilterModal}
                onHide={handleModalCancel}
                centered
                className="tablet-filter-modal"
            >
                <Modal.Header>
                    <Modal.Title>Filter</Modal.Title>

                    <button
                        type="button"
                        className="modal-close-btn"
                        onClick={handleModalCancel}
                    >
                        ✕
                    </button>
                </Modal.Header>

                <Modal.Body>
                    <FilterDropdown
                        id="department"
                        label="Department"
                        placeholder="Select Department"
                        options={tempDepartmentOptions}
                        setter={setTempDepartmentOptions}
                        optionKey="value"
                        isOpen={openDropdown === "department"}
                        onToggleDropdown={toggleDropdown}
                        onToggleOption={toggleTempOption}
                    />

                    <FilterDropdown
                        id="designation"
                        label="Designation"
                        placeholder="Select Designation"
                        options={tempDesignationOptions}
                        setter={setTempDesignationOptions}
                        optionKey="value"
                        isOpen={openDropdown === "designation"}
                        onToggleDropdown={toggleDropdown}
                        onToggleOption={toggleTempOption}
                    />

                    <div className="tablet-last-tracked-filter">
                        <Form.Label>Last Tracked Less Than</Form.Label>

                        <div className="tablet-last-tracked-control">
                            <Image
                                src="/images/dashboard/calendar_icon.svg"
                                alt="Calendar"
                                className="tablet-last-tracked-calendar-icon"
                                style={{ position: "relative", left: "10px", zIndex: "10" }}
                                onClick={() => tabletDatePickerRef.current?.setOpen(true)}

                            />
                            <DatePicker
                                ref={tabletDatePickerRef}
                                selected={tempLastTrackedFilterDate}
                                onChange={handleTempLastTrackedFilterChange}
                                renderCustomHeader={renderDatePickerHeader}
                                showTimeSelect
                                timeIntervals={1}
                                timeCaption="Time"
                                dateFormat="dd-MM-yyyy hh:mm aa"
                                placeholderText="dd-MM-yyyy hh:mm AM/PM"
                                maxDate={new Date()}
                                minTime={getStartOfDay(
                                    tempLastTrackedFilterDate || new Date()
                                )}
                                maxTime={getMaxTime(
                                    tempLastTrackedFilterDate
                                )}
                                filterTime={(time) =>
                                    isSelectableTime(
                                        time,
                                        tempLastTrackedFilterDate
                                    )
                                }
                                popperPlacement="bottom-start"
                                popperClassName="employee-lasttracked-popper tablet-datepicker-popper"
                                calendarClassName="employee-lasttracked-calendar"
                                wrapperClassName="employee-lasttracked-datepicker-wrapper"
                                autoComplete="off"
                            />

                            {tempLastTrackedLessThan && (
                                <button
                                    type="button"
                                    className="tablet-last-tracked-clear"
                                    onClick={() =>
                                        setTempLastTrackedLessThan("")
                                    }
                                    aria-label="Clear last tracked filter"
                                >
                                    ×
                                </button>
                            )}
                        </div>

                        {tempLastTrackedLessThan && (
                            <small>
                                {formatDateTimeLabel(
                                    tempLastTrackedLessThan
                                )}
                            </small>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        className="modal-cancel-btn"
                        onClick={handleModalCancel}
                    >
                        Cancel
                    </Button>

                    <Button
                        className="modal-apply-btn"
                        onClick={handleModalApply}
                    >
                        Apply
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EmployeeLasttracked;